import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  Users,
  Settings,
  ChevronRight,
  LayoutDashboard,
  Phone,
  Ticket,
  BarChart3,
  UserCheck,
  CreditCard,
  Landmark,
  Wallet,
  AlertTriangle,
  Megaphone,
  Bell,
  MessageCircle,
  Globe,
  TrendingUp,
  Settings2,
  Database,
  History,
  Map,
  Inbox,
  ClipboardList,
  ListTodo,
  Percent,
} from "lucide-react";
import useToken from "../../../hooks/auth/useToken";
import { chatService } from "../../../services/support/chatService";
import useTasksUnreadCount from "../../../hooks/support/tasks/useTasksUnreadCount";

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

interface MenuItem {
  icon: React.ComponentType<any>;
  label: string;
  route: string;
  badge?: any;
  externe?: boolean;
  category: "support" | "admin";
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, setIsCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();
  const [chatUnread, setChatUnread] = useState(0);
  const { count } = useTasksUnreadCount();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const refresh = async () => {
      try {
        const count = await chatService.getUnreadCount(token);
        if (!cancelled) setChatUnread(Number(count) || 0);
      } catch {
        /* Le badge n'est pas bloquant. */
      }
    };

    refresh();
    const id = window.setInterval(refresh, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token, location.pathname]);

  const supportMenuItems: MenuItem[] = [
    { icon: LayoutDashboard, label: "Tableau de bord", route: "/dashboard", category: "support" },
    { icon: Phone, label: "Centre d'appels", route: "/calls", category: "support" },
    { icon: Ticket, label: "Tickets", route: "/tickets", badge: null, category: "support" },
    {
      icon: ListTodo,
      label: "Les tâches à faire",
      route: "/tasks",
      category: "support",
      badge: count > 0 ? count : null,
    },
    { icon: Users, label: "Répertoire clients", route: "/clients", category: "support" },
    { icon: History, label: "Traçabilité utilisateur", route: "/audit-logs", category: "support" },
    { icon: MessageCircle, label: "Assistance chat", route: "/chat", category: "support", badge: chatUnread || null },
    { icon: Database, label: "Centre de connaissance", route: "/knowledge", category: "support" },
  ];

  const adminMenuItems: MenuItem[] = [
    { icon: UserCheck, label: "Utilisateurs / KYC", route: "/users", category: "admin" },
    { icon: CreditCard, label: "Transactions", route: "/transactions", category: "admin" },
    { icon: Landmark, label: "Virements bancaires", route: "/virements-bancaires/canada-cmr", category: "admin" },
    { icon: Wallet, label: "Wallets", route: "/wallets", category: "admin" },
    { icon: AlertTriangle, label: "Alertes fraude", route: "/fraud-alerts", category: "admin" },
    { icon: Megaphone, label: "Annonces Système", route: "/announcements-system", category: "admin" },
    { icon: Bell, label: "Notifications", route: "/notifications", category: "admin" },
    { icon: Globe, label: "Concurrents", route: "/concurrents", category: "admin" },
    { icon: Percent, label: "Taux du jour", route: "/daily-rates", category: "admin" },
    { icon: Map, label: "Transferts pays ", route: "/country-transferts", category: "admin" },
    { icon: Inbox, label: "Demandes de retrait", route: "/withdrawal-requests", category: "admin" },

    { icon: TrendingUp, label: "Buy and Sell", route: "/buy-and-sell", category: "admin" },
    { icon: Settings2, label: "Paramètres", route: "/settings", category: "admin" },
    { icon: Users, label: "Suivi des ambassadeurs", route: "/ambassadors", category: "admin" },
  ];

  const handleNavigation = (item: MenuItem) => {
    if (item.externe) {
      window.open(`${item.route}/redirect?token=${token}`, "_blank");
    } else {
      const basePath = item.category === "admin" ? "/admin" : "/support";
      navigate(`${basePath}${item.route}`);
    }
  };

  const isActiveRoute = (route: string) => {
    return location.pathname.includes(route);
  };

  const renderMenuItems = (items: MenuItem[]) => {
    return items.map((item, index) => {
      const Icon = item.icon;
      const isActive = isActiveRoute(item.route);

      return (
        <li key={index}>
          <button
            onClick={() => handleNavigation(item)}
            className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
              isActive
                ? "text-green-700 bg-green-50 border-r-2 border-green-600"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
            }`}
          >
            <Icon className="flex-shrink-0 w-5 h-5" />
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                )}
                {item.externe && <ChevronRight className="w-4 h-4" />}
              </>
            )}
          </button>
        </li>
      );
    });
  };

  return (
    <div
      className={`bg-white border-r border-gray-200 transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-72"
      } h-full flex flex-col`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className={`flex ${isCollapsed ? "justify-center w-full" : "items-center space-x-2"}`}>
          <img src="/logo.png" alt="Izilife Pay" className="w-8 h-8 object-contain" />
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="font-bold text-gray-800">Izilife Pay</span>
              <span className="text-xs text-gray-500">Service client</span>
            </div>
          )}
        </div>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {!isCollapsed && (
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 mt-2">Support Client</div>
        )}
        <ul className="space-y-1 mb-6">{renderMenuItems(supportMenuItems)}</ul>

        {!isCollapsed && <div className="border-t border-gray-200 my-4"></div>}

        {!isCollapsed && (
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Administration</div>
        )}
        <ul className="space-y-1">{renderMenuItems(adminMenuItems)}</ul>
      </nav>
    </div>
  );
};
